import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { of, startWith, switchMap } from 'rxjs';

import { FormlyFieldConfig } from '@ngx-formly/core';
import { MatError } from '@angular/material/form-field';
import { FormlyErrorService } from '../services/formly.service';

@Component({
  selector: 'app-formly-error',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatError],
  template: `
    @if (showError()) {
      <mat-error
        class="px-4 text-sm mb-4"
        data-cy="formly-error"
        role="alert"
        aria-live="assertive"
        aria-atomic="true"
        >{{ this.formlyErrorService.getErrorMessage(this.field()) }}</mat-error
      >
    }
  `,
})
export class FormlyErrorComponent {
  field = input.required<FormlyFieldConfig>();

  /**
   * Re-evaluation trigger for `showError`. That computed reads plain FormControl state
   * (errors / touched / dirty), which no signal tracks: over `field()` alone it was memoised after
   * its first run, so a host rendered unconditionally (secure-input, the identity-document
   * composite's per-subfield errors) never showed an error that only appeared LATER — a required
   * field blurred empty stayed silent. Hosts wrapped in `@if (formControl.errors)` (custom-select)
   * only worked by accident: the component was created after the error existed.
   * `AbstractControl.events` emits on every value, status, touched and pristine change, which is
   * exactly the set of inputs `showError` reads.
   */
  readonly #controlEvents = toSignal(
    toObservable(this.field).pipe(
      switchMap(field => (field.formControl ? field.formControl.events.pipe(startWith(null)) : of(null)))
    )
  );

  /**
   * Show only once the control is touched, not already when it is dirty. For a field that writes
   * its control on every keystroke (the licence plate) "dirty" is the first letter, and a required
   * error on the first letter tells the user off for something they are still doing.
   */
  requireTouched = input(false);

  showError = computed(() => {
    this.#controlEvents();
    const control = this.field().formControl;
    return !!control?.errors && (control.touched || (!this.requireTouched() && control.dirty));
  });

  formlyErrorService = inject(FormlyErrorService);
}
